import {
  isMosaicTrafficLightsValue,
  isMosaicTrafficLightSwitch,
  MosaicTrafficLightsValue,
  MosaicTrafficLightSwitch,
} from "aic";
import {
  isTlfLinkLocation,
  TlfLinkLocation,
} from "components/tlfpage/tlfheaderinfo/TlfLinkLocation";
import { eHierarchy, eHierarchyLevel, eLinkType, eReleaseStatus, eTlfItemType } from "../enums";

export interface ITlfItemRelationship extends Record<string, unknown> {
  id: number;
  linkId: string;
  linkType: eLinkType;
  azsolId: string;
  version: number;
  linkLocation?: TlfLinkLocation;
  linkStatus?: eReleaseStatus;
}

/**
 * Type guard that asserts whether a value conforms to the ITlfItemRelationship shape.
 *
 * @param obj - The object to validate as an ITlfItemRelationship
 * @returns boolean
 */
export const isITlfItemRelationship = (obj: unknown): obj is ITlfItemRelationship => {
  return (
    // Check object
    typeof obj === "object" &&
    obj !== null &&
    // Check properties exist
    "linkId" in obj &&
    "linkType" in obj &&
    "azsolId" in obj &&
    "version" in obj &&
    // Check properties types
    typeof (obj as ITlfItemRelationship).linkId === "string" &&
    typeof (obj as ITlfItemRelationship).linkType === "string" &&
    typeof (obj as ITlfItemRelationship).azsolId === "string" &&
    typeof (obj as ITlfItemRelationship).version === "number" &&
    // Check enums
    (obj as ITlfItemRelationship).linkType in eLinkType &&
    ((obj as ITlfItemRelationship).linkLocation === undefined ||
      (obj as ITlfItemRelationship).linkLocation === null ||
      isTlfLinkLocation((obj as ITlfItemRelationship).linkLocation)) &&
    ((obj as ITlfItemRelationship).linkStatus === undefined ||
      (obj as ITlfItemRelationship).linkStatus === null ||
      (obj as ITlfItemRelationship).linkStatus! in eReleaseStatus)
  );
};

export interface HierarchyLink {
  hierarchyId: string;
  hierarchyTag: string;
  hierarchyLevel: eHierarchyLevel;
}

/**
 * Type guard that asserts whether a value conforms to the HierarchyLink shape.
 *
 * @param obj - The object to validate as a HierarchyLink
 * @returns Whether the object is a valid HierarchyLink
 */
export const isHierarchyLink = (obj: unknown): obj is HierarchyLink => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "hierarchyId" in obj &&
    "hierarchyTag" in obj &&
    "hierarchyLevel" in obj &&
    typeof (obj as HierarchyLink).hierarchyId === "string" &&
    typeof (obj as HierarchyLink).hierarchyTag === "string" &&
    Object.values(eHierarchyLevel).includes(
      (obj as HierarchyLink).hierarchyLevel as eHierarchyLevel,
    )
  );
};

export interface TlfTag {
  /** Unique identifier (GUID) of the tag. */
  tagId: string;
  /** Display value of the tag. */
  tagValue: string;
}

/**
 * Type guard that asserts whether a value conforms to the TlfTag shape.
 *
 * @param obj - The object to validate as a TlfTag
 * @returns Whether the object is a valid TlfTag
 */
export const isTlfTag = (obj: unknown): obj is TlfTag => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "tagId" in obj &&
    "tagValue" in obj &&
    typeof (obj as TlfTag).tagId === "string" &&
    typeof (obj as TlfTag).tagValue === "string"
  );
};

export interface TlfItemIndexReadDto extends Record<string, unknown> {
  id: string;
  azsolId: string;
  version: number;

  primaryHierarchyId: string;
  primaryHierarchyName: string;
  primaryHierarchyLevel: eHierarchyLevel;
  primaryHierarchyType: eHierarchy;
  taPrefix: string;
  projectCode: string;
  dNumber: string;
  hierarchyLinks: HierarchyLink[];

  tocOrder?: number;
  tocNumber?: string;
  tocLabel?: string;
  /** Release status of the related Template */
  tocReleaseStatus?: eReleaseStatus;
  tocOriginTrafficLight?: MosaicTrafficLightsValue;
  coreTrafficLight: MosaicTrafficLightsValue;
  coreTrafficLightSwitch: MosaicTrafficLightSwitch;

  additionalInformation: string;
  type: eTlfItemType;
  domain: string;
  tlfTags?: TlfTag[];

  releaseDate?: string;
  decommissionDate?: string;
  releaseStatus: eReleaseStatus;

  relationships: ITlfItemRelationship[];
  isBackupVersion: boolean;

  lastUpdatedFromHistory: string;
}

/**
 * Type guard that asserts whether a value conforms to the TlfItemIndexReadDto shape.
 *
 * @param obj - The object to validate as a TlfItemIndexReadDto
 * @returns Whether the object is a valid TlfItemIndexReadDto
 */
export const isTlfItemIndexReadDto = (obj: unknown): obj is TlfItemIndexReadDto => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check props
  const chkObj = obj as TlfItemIndexReadDto;
  if (typeof chkObj.id !== "string") return false;
  if (typeof chkObj.azsolId !== "string") return false;
  if (typeof chkObj.version !== "number") return false;
  if (typeof chkObj.primaryHierarchyId !== "string") return false;
  if (typeof chkObj.primaryHierarchyName !== "string") return false;
  if (typeof chkObj.primaryHierarchyLevel !== "string") return false;
  if (typeof chkObj.primaryHierarchyType !== "string") return false;
  if (typeof chkObj.dNumber !== "string") return false;
  if (typeof chkObj.taPrefix !== "string") return false;
  if (typeof chkObj.projectCode !== "string") return false;
  if (chkObj.tocOrder !== undefined && typeof chkObj.tocOrder !== "number") return false;
  if (chkObj.tocNumber !== undefined && typeof chkObj.tocNumber !== "string") return false;
  if (chkObj.tocLabel !== undefined && typeof chkObj.tocLabel !== "string") return false;
  if (chkObj.tocReleaseStatus !== undefined && typeof chkObj.tocReleaseStatus !== "string")
    return false;
  if (
    chkObj.tocOriginTrafficLight !== undefined &&
    typeof chkObj.tocOriginTrafficLight !== "string"
  )
    return false;
  if (!isMosaicTrafficLightSwitch(chkObj.coreTrafficLightSwitch)) return false;
  if (!isMosaicTrafficLightsValue(chkObj.coreTrafficLight)) return false;
  if (typeof chkObj.additionalInformation !== "string") return false;
  if (typeof chkObj.type !== "string") return false;
  if (typeof chkObj.domain !== "string") return false;
  if (chkObj.releaseDate !== undefined && typeof chkObj.releaseDate !== "string") return false;
  if (chkObj.decommissionDate !== undefined && typeof chkObj.decommissionDate !== "string")
    return false;
  if (typeof chkObj.releaseStatus !== "string") return false;
  if (!Array.isArray(chkObj.relationships) || !chkObj.relationships.every(isITlfItemRelationship))
    return false;
  if (typeof chkObj.isBackupVersion !== "boolean") return false;
  if (!Array.isArray(chkObj.hierarchyLinks) || !chkObj.hierarchyLinks.every(isHierarchyLink))
    return false;
  if (typeof chkObj.lastUpdatedFromHistory !== "string") return false;
  if (
    chkObj.tlfTags !== undefined &&
    (!Array.isArray(chkObj.tlfTags) || !chkObj.tlfTags.every(isTlfTag))
  )
    return false;
  // Everything is good
  return true;
};
